/**
 * CSV export strategy implementation.
 *
 * Produces comma-separated values format with:
 * - Header row from column labels
 * - Formatted data rows based on column format types
 */

import type { ColumnDefinition, ExportStrategy } from '../types';
import { formatValue } from '../formatters';

/**
 * Escapes a value for CSV format.
 *
 * Wraps values in quotes if they contain commas, quotes, or newlines.
 * Doubles any embedded quotes as per CSV RFC 4180.
 */
function escapeCsvValue(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

/**
 * CSV export strategy.
 *
 * Converts tabular data to CSV format with proper escaping and formatting.
 */
export class CsvExportStrategy implements ExportStrategy {
    /**
     * Exports data to a CSV Blob.
     *
     * @param data - Array of data objects to export
     * @param columns - Column definitions for headers and formatting
     * @returns Blob with CSV content (text/csv MIME type)
     */
    export(data: unknown[], columns: ColumnDefinition[]): Blob {
        const rows: string[] = [];

        // Header row from column labels
        const headerRow = columns.map((col) => escapeCsvValue(col.label)).join(',');
        rows.push(headerRow);

        // Data rows with formatted values
        for (const item of data) {
            const record = item as Record<string, unknown>;
            const values = columns.map((col) => {
                const rawValue = record[col.key];
                const formatted = formatValue(rawValue, col.format);
                return escapeCsvValue(formatted);
            });
            rows.push(values.join(','));
        }

        const csvContent = rows.join('\n');
        return new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    }
}
